import React, { useEffect, useState } from 'react';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import ListFilters, { FilterField } from '../components/shared/list-filters';
import { TableSkeleton } from '../components/shared/skeletons';
import { useAgreementsController } from './context/agreements-controller';
import { useAgreementsState } from './context/agreements-context';
import { IAgreementResponse } from '../../model/base/response/agreement-response';

export function AgreementsComponent() {
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadAgreements } = useAgreementsController();
  const { agreements, loading, error } = useAgreementsState();

  const [filters, setFilters] = useState({
    term: '',
    startDate: undefined,
    endDate: undefined,
    year: undefined,
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    loadAgreements(filters);
  }, [filters]);

  const filterFields: FilterField[] = [
    {
      name: 'term',
      label: 'Pesquisar',
      type: 'text',
      placeholder: 'Buscar por órgão ou unidade',
      gridSize: { xs: 12, md: 6 },
    },

    {
      name: 'startDate',
      label: 'Data Inicial',
      type: 'date',
      gridSize: { xs: 12, md: 3 },
    },
    {
      name: 'endDate',
      label: 'Data Final',
      type: 'date',
      gridSize: { xs: 12, md: 3 },
    },
  ];

  const handleFilterChange = (name: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      page: 0,
    }));
  };

  const handleSearch = () => {
    loadAgreements(filters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      term: '',
      startDate: undefined,
      endDate: undefined,
      year: undefined,
      page: 0,
      pageSize: 10,
    };
    setFilters(clearedFilters);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleViewDetails = (id: number) => {
    navigate(`/convenios-repasses/detalhes/${id}`);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatCurrency = (value?: number) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title="Convênios e Repasses"
          subtitle="Consulte os convênios e repasses firmados pelo município"
          breadcrumbs={[{ label: 'Convênios e Repasses' }]}
        />

        <ListFilters
          fields={filterFields.map((field) => ({
            ...field,
            value: filters[field.name as keyof typeof filters],
          }))}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onClear={handleClearFilters}
        />

        {loading ? (
          <TableSkeleton rows={10} columns={6} hasActions />
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : agreements.items.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Nenhum convênio ou repasse encontrado.
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper} elevation={3} sx={{ mt: 3, borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Órgão Superior</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Órgão Subordinado</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Unidade Gestora</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Data Início</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Data Fim</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Valor Repasse</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agreements.items.map((agreement: IAgreementResponse) => (
                    <TableRow
                      key={agreement.id}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          cursor: 'pointer',
                        },
                      }}
                    >
                      <TableCell>{agreement.superiorBody || '-'}</TableCell>
                      <TableCell>{agreement.subordinateBody || '-'}</TableCell>
                      <TableCell>{agreement.managementUnit || '-'}</TableCell>
                      <TableCell>{formatDate(agreement.startDate)}</TableCell>
                      <TableCell>{formatDate(agreement.endDate)}</TableCell>
                      <TableCell>{formatCurrency(agreement.transferAmount)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver Detalhes" arrow>
                          <IconButton
                            color="primary"
                            onClick={() => handleViewDetails(agreement.id)}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                              },
                            }}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {agreements.pageCount > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 3 }}>
                <Pagination
                  count={agreements.pageCount}
                  page={agreements.page}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </PageContainer>
  );
}
